import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import { Form, Formik, FormikProps } from 'formik';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { ClergyBioChurchProfessional, DataState } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { ApiStatuses } from '../../../global/models/api-model';
import { useApiResponseError } from '../../../global/hooks/useApiError';
import { setClergyBioProfessional, setLoadingState } from '../clergy-bio-slice';
import { getClergyBioProfessional, patchClergyBioProfessional } from '../../../global/ducks/clergy-bio-api';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { FormTypes } from '../../account-info/models/form-models';
import EcdMessage from '../summary/ecd-message';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { ClergyBioProfessionalFormModel } from './models/form-models';
import { ConstructPayload, CreateNewProfessionalItem, GetClergyBioProfessionalValidationSchema } from './clergy-bio-professional-helpers';
import { FormField } from '../../../global/components/form-element/form-field';
import ClergyBioPublicationItem from './clergy-bio-publication-item';
import ClergyBioHonorItem from './clergy-bio-honor-item';
import ClergyBioSocietyItem from './clergy-bio-society-item';
import { PageHeader } from '../../../global/components/layout/page-header';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { ClergyBioGaFormTypes, HandleClergyBioSubmit } from '../../../global/helpers/analytics-helper';



function ClergyBioProfessionalUpdate() {

	const buttonClasses = buttonStyles();
	const settings = useSelector<TcsmStates, ClergyBioSettings>(state => state?.epiProps?.clergyBioSettingsState?.epiContent);
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.professional);
	const professionals = useSelector<TcsmStates, ClergyBioChurchProfessional>(state => state?.clergyBioState?.professional);
	const guid = useSelector<TcsmStates, string>(state => state?.authenticatedState?.clientGuid);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state?.epiProps?.labelSettingState?.epiContent);
	const dispatch = useDispatch();
	const [updateStatus, setUpdateStatus] = useState<ApiStatuses>(ApiStatuses.None);
	const [errorMessage, setErrorMessage] = useState<string>(null);
	const defaultValues = {
		publications: _.cloneDeep(professionals?.publications?.details),
		honors: _.cloneDeep(professionals?.honors?.details),
		societies: _.cloneDeep(professionals?.societies?.details),
		optOut: _.cloneDeep(professionals?.optOut),
	};
	const [initialValues, setInitialValues] = useState<ClergyBioProfessionalFormModel>(
		{
			publications: [],
			honors: [],
			societies: [],
			optOut: false,
		},
	);
	const apiErrors = useApiResponseError();

    const onSuccess = (res) => {
		if (res?.status?.code === 'SUCCESS') {
			HandleClergyBioSubmit(ClergyBioGaFormTypes.Professional, guid);
            setUpdateStatus(ApiStatuses.Success);
        }
        if (res?.status?.code === 'ERROR') {
            setUpdateStatus(ApiStatuses.Failure);
            setErrorMessage(res?.status?.message);
        }
        dispatch(setClergyBioProfessional(res?.data));
    };

	const updateProfessional = (values: ClergyBioProfessionalFormModel) => {
		dispatch(patchClergyBioProfessional(guid, ConstructPayload(values), onSuccess));

	};
	const validationSchema = GetClergyBioProfessionalValidationSchema(settings, labelSettings);

	const addNewItem = (key: string) => {
		const updated = initialValues[key];
		updated.push(CreateNewProfessionalItem());
		const copy = { ...initialValues };
		copy[key] = updated;
		setInitialValues(copy);
	};

	useEffect(() => {

		setInitialValues(
			{
				publications: _.cloneDeep(professionals?.publications?.details),
				honors: _.cloneDeep(professionals?.honors?.details),
				societies: _.cloneDeep(professionals?.societies?.details),
				optOut: _.cloneDeep(professionals?.optOut),
			},
		);

	}, [professionals]);

	useEffect(() => {

		return function cleanUp() {
			dispatch(setLoadingState(DataState.LOADING, 'professional'));
			dispatch(getClergyBioProfessional(
				guid,
				(data) => dispatch(setClergyBioProfessional(data?.data)),
				() => dispatch(setLoadingState(DataState.ERROR, 'professional'))),
			);
		};
	}, []);

	function renderContent() {
		return (
			<Formik
				initialValues={initialValues}
				onSubmit={(values) => updateProfessional(values)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}
			>
				{(formProps: FormikProps<ClergyBioProfessionalFormModel>) => (

					<Form>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings?.submitText}
							cancelText={labelSettings?.cancelButtonText}
							onCancel={() => {
								dispatch(setLoadingState(DataState.LOADING, 'professional'));
								dispatch(getClergyBioProfessional(
									guid,
									(data) => dispatch(setClergyBioProfessional(data?.data)),
									() => dispatch(setLoadingState(DataState.ERROR, 'professional'))),
								);
								window.scrollTo(0, 0);
							}}
							initialValue={defaultValues}
							successfulForms={updateStatus === ApiStatuses.Success ? [FormTypes.CLERGY_BIO_PROFESSIONAL_UPDATE] : []}
						>
							<PageHeader headerText={settings?.professionalTitle} />
							<ContentSpacer>
								<EcdMessage />
							</ ContentSpacer>
							{
								updateStatus === ApiStatuses.Failure &&
								<ContentSpacer>
									<Banner
										banner={{
											type: 'error',
											label: <p><strong>{errorMessage}</strong></p>,
										}}
									/>
								</ContentSpacer>
							}
							<DividerSection sectionText={settings?.publicationsTitle} showCleryIcon >
								{
									initialValues?.publications?.map((p, idx) =>
										<ClergyBioPublicationItem
											key={`publications-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='publications'
											formikProps={formProps}
											professionalItem={p}
											setList={setInitialValues}
											defaultValue={defaultValues?.publications[idx]}
										/>)
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewItem('publications')} >{settings?.addPublicationButtonText}</button>

							</DividerSection>
							<DividerSection sectionText={settings?.honorsTitle} sectionDescription={settings?.honorUpdateDescription} showCleryIcon>
								{
									initialValues?.honors?.map((h, idx) =>
										<ClergyBioHonorItem
											key={`honors-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='honors'
											formikProps={formProps}
											professionalItem={h}
											setList={setInitialValues}
											defaultValue={defaultValues?.honors[idx]}
										/>)
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewItem('honors')} >{settings?.addHonorButtonText}</button>
							</DividerSection>
							<DividerSection sectionText={settings?.societiesTitle} sectionDescription={settings?.societyDescriptionLabel} showCleryIcon>
								{
									initialValues?.societies?.map((s, idx) =>
										<ClergyBioSocietyItem
											key={`societies-${idx}`}
											settings={settings}
											id={idx?.toString()}
											arrayField='societies'
											formikProps={formProps}
											professionalItem={s}
											setList={setInitialValues}
											defaultValue={defaultValues?.societies[idx]}
										/>)
								}
								<button className={buttonClasses.secondaryButton} type='button' onClick={() => addNewItem('societies')} >{settings?.addSocietyButtonText}</button>
							</DividerSection>
							<DividerSection removeDivider sectionText={settings?.noticeTitle} >
								<FormField
									name='optOut'
									label={settings?.professionalNoticeMessage}
									component='checkBox'
									touched={formProps.touched}
									errors={formProps.errors}
									value={formProps?.values?.optOut ?? false}
									checked={formProps?.values?.optOut ?? false}
								/>
							</DividerSection>
						</FormWrapper>
					</Form>

				)}
			</Formik>
		);
	}

	return (
		<ClergyBioComponentLoader
			state={loadingState}
			content={renderContent()}
		/>
	);
}


export default ClergyBioProfessionalUpdate;